import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart, Bar, /* XAxis, */ YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer } from 'recharts'

import { ChartData } from '../../types'
import { useIsMobile } from '../../hooks/use-mobile'
import { useStableCallback } from '../../hooks/use-stable-callback'
import { ChartContainer, ChartTooltipContent } from '../common/chart'

export const PaymentBatches = ({ numOfBatchesSent, numOfBatchesForReview, numOfBatchesToBeDisperse }: { numOfBatchesSent: number; numOfBatchesForReview: number; numOfBatchesToBeDisperse: number }) => {
  const isMobile = useIsMobile()
  const { t } = useTranslation(['insights-funding-history-management'])

  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [chartTicks, setChartTicks] = useState<Array<number>>([])
  const [chartDomain, setChartDomain] = useState<Array<number>>([0, 0])
  const [data, setData] = useState<Array<ChartData>>([
    { name: t('insights.charts.paymentBatches.conventions.toBeReviewed'), value: 0, color: '#9CA3AF' },
    { name: t('insights.charts.paymentBatches.conventions.readyToBeDispersed'), value: 0, color: '#6B30FF' },
    { name: t('insights.charts.paymentBatches.conventions.sent'), value: 0, color: '#22C55E' }
  ])

  useEffect(() => {
    const dataUpdated = [...data]
    dataUpdated[0].value = numOfBatchesForReview
    dataUpdated[1].value = numOfBatchesToBeDisperse
    dataUpdated[2].value = numOfBatchesSent

    const { max } = handleGetMinMax(numOfBatchesForReview, numOfBatchesSent, numOfBatchesToBeDisperse)
    const { result } = handleGetNormalizedDistance(0, max, 4)
    setChartTicks(result)
    setChartDomain([0, max])
    setData(dataUpdated)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numOfBatchesForReview, numOfBatchesSent, numOfBatchesToBeDisperse])

  const handleGetMinMax = (a: number, b: number, c: number): { min: number; max: number } => {
    const min = Math.min(a, b, c)
    const max = Math.max(a, b, c)

    return { min, max }
  }

  const handleGetNormalizedDistance = (min: number, max: number, divider: number): { result: number[]; newMax: number } => {
    if (divider <= 0) {
      throw new Error('Divider must be greater than 0')
    }

    const rawStep = (max - min) / divider
    const step = Math.ceil(rawStep)

    const newMax = min + step * divider

    const result: number[] = []

    for (let i = 0; i <= divider; i++) {
      result.push(min + i * step)
    }

    return { result, newMax }
  }

  const config = data.reduce((acc, item) => {
    acc[item.name] = { color: item.color, label: item.name }
    return acc
  }, {} as Record<string, { color: string; label: string }>)

  const handleMouseEnter = useStableCallback((_: any, index: number) => {
    setActiveIndex(index)
  })

  const handleMouseLeave = useStableCallback(() => {
    setActiveIndex(null)
  })

  return (
    <div
      className="border border-[#E5E7EB] bg-[#F9FAFB] self-stretch flex min-w-full flex-col items-stretch px-3 py-4 md:px-6 md:py-6 rounded-[24px] border-solid"
      style={{ height: '400px' }}
    >
      <div
        style={{
          fontFamily: 'Open Sans',
          fontSize: '16px',
          fontWeight: 700,
          lineHeight: '24px',
          color: '#107569'
        }}
      >
        {t('insights.charts.paymentBatches.title')}
      </div>
      <div className={`${isMobile ? 'h-[180px]' : 'h-[300px]'} w-full mt-4 md:mt-6`}>
        <ChartContainer
          config={config}
          className="h-full aspect-auto mt-0"
        >
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <BarChart
              data={data}
              margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
              barGap={20}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
              />
              {/* <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                fontSize={isMobile ? 10 : 12}
              /> */}
              <YAxis
                domain={chartDomain}
                ticks={chartTicks}
                axisLine={false}
                tickLine={false}
                fontSize={isMobile ? 10 : 12}
              />
              <Tooltip
                content={<ChartTooltipContent />}
                cursor={{ fill: 'transparent' }}
                wrapperStyle={{ outline: 'none' }}
              />
              <Bar
                dataKey="value"
                radius={[4, 4, 0, 0]}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    style={{
                      transform: activeIndex === index ? 'scaleY(1.1)' : 'scaleY(1)',
                      transformOrigin: 'bottom',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </div>

      <div className="self-center flex w-full max-w-full items-center justify-center mt-2 md:mt-6 gap-2 md:gap-4 text-xs md:text-sm text-[#737373] font-normal">
        {data.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-1 md:gap-2"
          >
            <div
              className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'} rounded-full`}
              style={{ backgroundColor: item.color }}
            />
            <div>{item.name}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
